/**
 * The reviewed proposal as an editable draft: built from the extraction's proposal, kept in this browser
 * (`ui/draftStorage`, per person and extraction) so a reload does not lose the review, and turned into the one
 * `POST /api/assets/from-extraction` body. Nothing is on the server until Save.
 */

import type { AssetKind, AssetMaturity, SectionAudience } from '@/api/types/assets';
import { FromExtractionSection } from '@/api/generated/schemas';
import type { FromExtractionIn, ProposalOut, ProposalSource } from '@/api/types/extractions';
import type { MaterialAudience, MaterialType } from '@/api/types/materials';
import { draftKey, readJsonDraft, removeDraft, writeDraft } from '@/ui/draftStorage';
import { fieldErrors } from '@/api/validate';
import { withLabel } from '@/ui/labelRules';

import { caseBody, caseErrors, caseErrorText, metricRow, type CaseForm } from '../cases/caseForm';
import { type AssetFieldsDraft, createBody, EMPTY_FIELDS, fieldsValid } from '../fields/assetFields';
import { materialValid, titleFromFilename } from '../materials/materialList';

export type ExtractedField = 'name' | 'tagline' | 'summary' | 'maturity' | 'industries' | 'partners' | 'countries';

export interface DraftSection {
  key: string;
  title: string;
  body: string;
  audience: SectionAudience;
  /** A template section the catalog keeps internal unless someone who reads internal content opens it. */
  internal: boolean;
  /** Where the extracted text comes from; null for a section the files did not fill. */
  source: ProposalSource | null;
}

export interface DraftCase {
  key: string;
  form: CaseForm;
  /** The first quote behind it, shown with the AI marker. */
  source: ProposalSource | null;
}

export interface DraftMaterial {
  file_id: string;
  filename: string;
  title: string;
  material_type: MaterialType | '';
  /** No default: who may see each file is chosen on purpose. */
  audience: MaterialAudience | '';
  include: boolean;
}

export interface FromFilesDraft {
  extractionId: string;
  fields: AssetFieldsDraft;
  /** The fields the AI filled, with the quote behind each. */
  extracted: Partial<Record<ExtractedField, ProposalSource>>;
  sections: DraftSection[];
  cases: DraftCase[];
  materials: DraftMaterial[];
}

export interface TemplateSection {
  key: string;
  title: string;
  internal: boolean;
}

function extractedFields(proposal: ProposalOut): FromFilesDraft['extracted'] {
  const f = proposal.fields;
  const found: FromFilesDraft['extracted'] = {};
  for (const key of ['name', 'tagline', 'summary', 'maturity'] as const) {
    const value = f[key];
    if (value) found[key] = value.source;
  }
  const firstOf = (list: { source: ProposalSource }[]) => list[0]?.source;
  const tags = { industries: firstOf(f.industries.filter((t) => t.id)), partners: firstOf(f.partners), countries: firstOf(f.countries) };
  for (const [key, source] of Object.entries(tags)) if (source) found[key as ExtractedField] = source;
  return found;
}

function fieldsFrom(proposal: ProposalOut): AssetFieldsDraft {
  const f = proposal.fields;
  return {
    ...EMPTY_FIELDS,
    name: f.name?.value ?? '',
    kind: proposal.kind as AssetKind,
    tagline: f.tagline?.value ?? '',
    summary: f.summary?.value ?? '',
    maturity: (f.maturity?.value as AssetMaturity | undefined) ?? EMPTY_FIELDS.maturity,
    industry_ids: f.industries.flatMap((t) => (t.id ? [t.id] : [])),
    // Partners are free names: the ones the catalog has and the new ones alike go in, and the save creates the new.
    partners: [...f.partners.map((t) => t.name), ...proposal.unknown_partners].reduce(withLabel, []),
    countries: f.countries.map((c) => c.code),
  };
}

function sectionsFrom(proposal: ProposalOut, template: TemplateSection[]): DraftSection[] {
  const proposed = new Map(proposal.sections.map((s) => [s.key, s]));
  return template.map((t) => {
    const found = proposed.get(t.key);
    return {
      key: t.key,
      title: t.title,
      body: found?.body ?? '',
      audience: found?.audience ?? (t.internal ? 'internal' : 'shareable'),
      internal: t.internal,
      source: found?.sources[0] ?? null,
    };
  });
}

function casesFrom(proposal: ProposalOut): DraftCase[] {
  return proposal.cases.map((c, i) => ({
    key: `case-${i + 1}`,
    source: Object.values(c.sources)[0] ?? c.metrics[0]?.source ?? null,
    form: {
      type: c.type,
      client_name: c.client_name,
      aliases: [],
      anonymized_label: c.anonymized_label,
      industry_id: '',
      country: '',
      status: c.status,
      situation: c.situation,
      objective: c.objective,
      approach: c.approach,
      achievements: c.achievements,
      metrics: c.metrics.map((m) => metricRow({ label: m.label, value: m.value, before: m.before, after: m.after, unit: m.unit, provenance: m.provenance })),
    },
  }));
}

export function draftFromProposal(proposal: ProposalOut, template: TemplateSection[]): FromFilesDraft {
  return {
    extractionId: proposal.id,
    fields: fieldsFrom(proposal),
    extracted: extractedFields(proposal),
    sections: sectionsFrom(proposal, template),
    cases: casesFrom(proposal),
    materials: proposal.files.map((f) => ({
      file_id: f.id,
      filename: f.filename,
      title: titleFromFilename(f.filename),
      material_type: '',
      audience: '',
      include: true,
    })),
  };
}

export function saveBody(draft: FromFilesDraft): FromExtractionIn {
  return {
    asset: createBody(draft.fields),
    sections: draft.sections
      .filter((s) => s.body.trim() !== '')
      .map((s) => ({ key: s.key, body: s.body.trim(), audience: s.audience })),
    cases: draft.cases.map((c) => caseBody(c.form)),
    materials: draft.materials
      .filter((m) => m.include)
      .map((m) => ({ file_id: m.file_id, title: m.title.trim(), material_type: m.material_type as MaterialType, audience: m.audience as MaterialAudience })),
  };
}

/** Why the draft cannot be saved yet, in reading order; empty when it can. */
export function draftProblems(draft: FromFilesDraft): string[] {
  const problems: string[] = [];
  if (!fieldsValid(draft.fields)) problems.push('Give the asset a name.');
  draft.sections
    .filter((s) => s.body.trim() !== '')
    .forEach((s) => Object.values(fieldErrors(SECTION, { body: s.body })).forEach((message) => problems.push(`${s.title}: ${message}`)));
  if (draft.materials.some((m) => m.include && !materialValid(m))) {
    problems.push('Choose a title, a type and an audience for each file, or leave the file out.');
  }
  draft.cases.forEach((c, i) => problems.push(...caseProblems(c.form).map((p) => `Success case ${i + 1}: ${p}`)));
  return problems;
}

/** The section text the reviewer can edit; key and title come from the kind's template. */
const SECTION = FromExtractionSection.pick({ body: true });

function caseProblems(form: CaseForm): string[] {
  const { anonymized_label: label, ...others } = caseErrors(form);
  const problems = label ? [label] : [];
  if (form.type === 'client' && !form.client_name.trim()) problems.push("add the client's name, or make it a benchmark.");
  return [...problems, ...Object.entries(others).map(([path, message]) => caseErrorText(path, message))];
}

// --- Kept in this browser ------------------------------------------------------------------------------------------

// Version 2: the fields hold partner names (`partners`) where version 1 held `partner_ids`.
export const storageKey = (userId: string, extractionId: string) => draftKey(userId, `extraction-${extractionId}`, 2);

/** Accepts a stored draft of this extraction (one written by this version of the review). */
export const isDraftOf =
  (extractionId: string) =>
  (value: unknown): value is FromFilesDraft => {
    const draft = value as Partial<FromFilesDraft> | null;
    return draft?.extractionId === extractionId && Array.isArray(draft.sections);
  };

export function loadDraft(userId: string, extractionId: string): FromFilesDraft | null {
  return readJsonDraft(storageKey(userId, extractionId), isDraftOf(extractionId));
}

export function storeDraft(userId: string, draft: FromFilesDraft): void {
  writeDraft(storageKey(userId, draft.extractionId), JSON.stringify(draft));
}

export function dropDraft(userId: string, extractionId: string): void {
  removeDraft(storageKey(userId, extractionId));
}
